import { useState } from 'react';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Grid,
    IconButton,
    ButtonGroup,
    Button,
    Chip,
    Tooltip,
    Typography,
    styled
} from '@material-ui/core';
import moment from 'moment';
import FancyCard from '../fancy/FancyCard';
import { MODAL_TYPE, useModals } from '../../hooks/useModal';
import { BOOKING_STATE_BORDER_COLORS } from '../../bookingStates';
import ReadonlyEditor from '../drafteditor/components/ReadonlyEditor';
import { formatMoney } from '../../utils';
import {
    ChevronDownIcon,
    CopyIcon,
    DeleteIcon,
    EditIcon,
    PrinterIcon,
    WarningIcon
} from '../../icons';
import ActionIconButton from '../atom/ActionIconButton';
import BookingFieldsTable from './BookingFieldsTable';
import { useBookingPermissions } from '../../hooks/useBookingPermissions';
import { useCancelWindow } from '../../hooks/useCancelWindow';
import { useNotify } from '../../hooks/useNotify';

const BookingItemDetailsLabel = styled(Typography)({
    margin: '8px 0',
    fontWeight: 'bold',
    fontSize: '1rem'
});

function BookingItemRow({
    bookingItem,
    isAgent,
    isAdmin,
    onCancelFinished,
    onGuestPage,
    open,
    setOpen,
    showModal
}) {
    const { canRefund, refundDisabledReason } = useBookingPermissions(bookingItem);
    const { isStartInsideCancelWindow } = useCancelWindow(bookingItem);
    const { notifySuccess } = useNotify();

    const handleCopyBookingId = e => {
        e.stopPropagation();
        navigator.clipboard.writeText(String(bookingItem.id));
        notifySuccess('Reservation ID copied to clipboard');
    };

    const canCancelAsAdmin =
        (isAgent || isAdmin) &&
        onCancelFinished &&
        (bookingItem.status === 'confirmed' || bookingItem.status === 'unconfirmed');
    const canPrint = bookingItem.status === 'confirmed' && bookingItem.print_url;

    const isGuest = !isAgent && !isAdmin;
    const isFutureBooking = moment(bookingItem.activity_start).isAfter(moment());
    const canCancelAsGuest =
        isGuest &&
        onGuestPage &&
        onCancelFinished &&
        (bookingItem.status === 'confirmed' || bookingItem.status === 'unconfirmed') &&
        isFutureBooking;

    // Cancel action requires refund capability since cancellation involves refunds
    const canCancelWithCapability = canCancelAsAdmin && canRefund;

    // Determine if there are any booking notes or details to display (keep it always up to date with the below code)
    const bookingNotesOrDetails =
        bookingItem.agentnote?.agentnotes?.length > 0 ||
        bookingItem.agentnote?.checkinLocation ||
        bookingItem.agentnote?.checkinTime ||
        bookingItem.agentnote?.pickupLocation ||
        bookingItem.agentnote?.pickupTime ||
        bookingItem.booking_info?.confirmationMessage;

    if (!bookingItem.activity) {
        return null;
    }

    return (
        <FancyCard barColor={BOOKING_STATE_BORDER_COLORS[bookingItem.status]} key={bookingItem.id}>
            <Accordion
                key={bookingItem.id}
                expanded={open === bookingItem.id}
                onChange={() => {
                    // if currently open, close it
                    setOpen(open === bookingItem.id ? false : bookingItem.id);
                }}
            >
                <AccordionSummary
                    expandIcon={
                        <IconButton>
                            <ChevronDownIcon />
                        </IconButton>
                    }
                >
                    <Grid container>
                        <Grid item xs={10}>
                            <Grid container spacing={2}>
                                <Grid container item xs={8} spacing={2}>
                                    <Grid
                                        item
                                        md={12}
                                        lg={bookingNotesOrDetails ? 7 : 12}
                                        xl={bookingNotesOrDetails ? 6 : 12}
                                    >
                                        <h2>{`${bookingItem.activity?.company?.name} (${bookingItem.activity?.island?.name})`}</h2>
                                        <h2 style={{ fontWeight: 'normal' }}>
                                            {bookingItem.activity?.name}
                                        </h2>
                                        <p>
                                            {moment(bookingItem.activity_start).format(
                                                'ddd MMM DD, YYYY [at] h:mm A'
                                            )}
                                        </p>
                                        <p>
                                            <b>Reservation ID:</b>
                                            <br />
                                            {bookingItem.id}
                                            <Tooltip title="Copy Reservation ID">
                                                <IconButton
                                                    size="small"
                                                    onClick={handleCopyBookingId}
                                                    style={{ marginLeft: 4 }}
                                                >
                                                    <CopyIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </p>
                                        <p>
                                            <b>Tickets</b>
                                            {bookingItem.tickets && (
                                                <ul style={{ margin: 0 }}>
                                                    {bookingItem.tickets.map(ticket => (
                                                        <li key={ticket?.id}>
                                                            {ticket?.quantity} x {ticket?.name}
                                                        </li>
                                                    ))}
                                                </ul>
                                            )}
                                        </p>
                                        <h5
                                            style={{
                                                color: bookingItem?.status === 'denied' ? 'red' : ''
                                            }}
                                        >
                                            {bookingItem?.status === 'cancelled_full_refund'
                                                ? 'cancelled'
                                                : bookingItem?.status}
                                        </h5>
                                    </Grid>
                                    {bookingNotesOrDetails && (
                                        <Grid item md={12} lg={5} xl={6}>
                                            {bookingItem.booking_info?.confirmationMessage ? (
                                                <div>
                                                    <BookingItemDetailsLabel>
                                                        Vendor note:
                                                    </BookingItemDetailsLabel>
                                                    <Typography variant="body1">
                                                        {
                                                            bookingItem.booking_info
                                                                .confirmationMessage
                                                        }
                                                    </Typography>
                                                </div>
                                            ) : null}
                                            {bookingItem.agentnote?.agentnotes?.length > 0 ? (
                                                <div>
                                                    <BookingItemDetailsLabel>
                                                        Agent notes:
                                                    </BookingItemDetailsLabel>
                                                    {bookingItem.agentnote.agentnotes.map(
                                                        (note, idx) => (
                                                            <div
                                                                key={note.id || idx}
                                                                style={{
                                                                    marginBottom: '15px'
                                                                }}
                                                            >
                                                                <h5
                                                                    style={{
                                                                        marginBottom: '1px'
                                                                    }}
                                                                >
                                                                    {note.created_at
                                                                        ? moment(
                                                                              note.created_at
                                                                          ).format(
                                                                              'MMM D, YYYY [at] h:mm A'
                                                                          )
                                                                        : 'Not saved yet'}
                                                                </h5>
                                                                <Typography variant="body1">
                                                                    {note.note}
                                                                </Typography>
                                                            </div>
                                                        )
                                                    )}
                                                </div>
                                            ) : null}
                                            {bookingItem.agentnote?.checkinLocation ||
                                            bookingItem.agentnote?.checkinTime ? (
                                                <div>
                                                    <BookingItemDetailsLabel>
                                                        Check In:
                                                    </BookingItemDetailsLabel>
                                                    <Typography variant="body1">
                                                        {[
                                                            bookingItem.agentnote?.checkinLocation,
                                                            bookingItem.agentnote?.checkinTime
                                                        ]
                                                            .filter(e => e)
                                                            .join(', ')}
                                                    </Typography>
                                                </div>
                                            ) : null}
                                            {bookingItem.agentnote?.pickupLocation ||
                                            bookingItem.agentnote?.pickupTime ? (
                                                <div>
                                                    <BookingItemDetailsLabel>
                                                        Pickup:
                                                    </BookingItemDetailsLabel>
                                                    <Typography variant="body1">
                                                        {[
                                                            bookingItem.agentnote?.pickupLocation,
                                                            bookingItem.agentnote?.pickupTime
                                                        ]
                                                            .filter(e => e)
                                                            .join(', ')}
                                                    </Typography>
                                                </div>
                                            ) : null}
                                        </Grid>
                                    )}
                                </Grid>
                                {bookingItem.status === 'confirmed' && (
                                    <Grid container item xs={4}>
                                        {bookingItem?.booking_info?.confirmationCode && (
                                            <Grid item xs={12}>
                                                {`${bookingItem.booking_info.confirmationCode}`.startsWith(
                                                    'https://'
                                                ) ? (
                                                    <>
                                                        <h5>Confirmation link:</h5>
                                                        <a
                                                            target="_blank"
                                                            href={
                                                                bookingItem.booking_info
                                                                    .confirmationCode
                                                            }
                                                            rel="noreferrer"
                                                        >
                                                            Open external page
                                                        </a>
                                                        <br />
                                                        <small
                                                            style={{
                                                                wordBreak: 'break-all',
                                                                fontSize: '60%',
                                                                color: '#999'
                                                            }}
                                                        >
                                                            {
                                                                bookingItem.booking_info
                                                                    .confirmationCode
                                                            }
                                                        </small>
                                                    </>
                                                ) : (
                                                    <>
                                                        <h5>Confirmation code:</h5>
                                                        <Typography
                                                            variant="body1"
                                                            style={{ fontSize: 16 }}
                                                        >
                                                            {
                                                                bookingItem.booking_info
                                                                    .confirmationCode
                                                            }
                                                        </Typography>
                                                    </>
                                                )}
                                            </Grid>
                                        )}
                                        {bookingItem.booking?.agent && (
                                            <Grid item xs={12}>
                                                <h5>Booking agent:</h5>
                                                <Typography
                                                    variant="body1"
                                                    style={{ fontSize: 16 }}
                                                >{`${bookingItem.booking.agent?.firstname} ${bookingItem.booking.agent?.lastname}`}</Typography>
                                            </Grid>
                                        )}
                                    </Grid>
                                )}
                            </Grid>
                        </Grid>
                        <Grid item xs={2}>
                            <ButtonGroup
                                orientation="horizontal"
                                color="primary"
                                size="small"
                                fullWidth
                            >
                                {canPrint && (
                                    <ActionIconButton
                                        title="Print voucher"
                                        icon={PrinterIcon}
                                        onClick={() => {
                                            showModal(MODAL_TYPE.SELECT_PRINT, {
                                                printUrl: bookingItem.print_url
                                            });
                                        }}
                                    />
                                )}
                                {isAgent && (
                                    <ActionIconButton
                                        title="Modify booking"
                                        icon={EditIcon}
                                        to={`/client/bookings/${bookingItem.id}`}
                                    />
                                )}
                                {canCancelAsAdmin && (
                                    <ActionIconButton
                                        title={
                                            canCancelWithCapability
                                                ? 'Cancel reservation'
                                                : refundDisabledReason || 'Cannot cancel'
                                        }
                                        icon={DeleteIcon}
                                        disabled={!canCancelWithCapability}
                                        onClick={() => {
                                            showModal(MODAL_TYPE.CANCEL_BOOKINGITEM, {
                                                bookingItem,
                                                onCancelFinished
                                            });
                                        }}
                                    />
                                )}
                                {canCancelAsGuest && (
                                    <ActionIconButton
                                        title={
                                            isStartInsideCancelWindow
                                                ? 'Cancel (No Refund)'
                                                : 'Cancel Booking'
                                        }
                                        icon={isStartInsideCancelWindow ? WarningIcon : DeleteIcon}
                                        color={isStartInsideCancelWindow ? '#e65100' : undefined}
                                        onClick={() => {
                                            showModal(MODAL_TYPE.CANCEL_BOOKINGITEM, {
                                                bookingItem,
                                                onCancelFinished
                                            });
                                        }}
                                    />
                                )}
                            </ButtonGroup>
                        </Grid>
                    </Grid>
                </AccordionSummary>
                <AccordionDetails>
                    <Grid container spacing={2}>
                        <Grid item container>
                            <Grid item container spacing={2} style={{ paddingBottom: '0.5rem' }}>
                                <Grid item>
                                    <strong>Charge: </strong>
                                    {formatMoney(bookingItem.amount)}
                                </Grid>
                            </Grid>
                            {isAgent && bookingItem.booking.booking_discounts?.length > 0 && (
                                <Grid
                                    item
                                    container
                                    spacing={2}
                                    style={{ paddingBottom: '0.5rem' }}
                                >
                                    <Grid item>
                                        {bookingItem.booking.booking_discounts.map(discount => (
                                            <Chip
                                                key={discount.id}
                                                label={`${discount.discount_package?.name ||
                                                    'Discount'} - ${formatMoney(discount.amount)}`}
                                                size="small"
                                                style={{
                                                    backgroundColor: '#05AD8E',
                                                    color: 'white',
                                                    marginRight: '5px'
                                                }}
                                            />
                                        ))}
                                    </Grid>
                                </Grid>
                            )}
                            {bookingItem.activity?.description && (
                                <Grid item xs={12}>
                                    <h5>Activity Details</h5>
                                    <Grid
                                        item
                                        xs={12}
                                        style={{
                                            whiteSpace: 'pre-line',
                                            marginTop: 0,
                                            marginBottom: '10px',
                                            fontSize: '0.875rem'
                                        }}
                                    >
                                        <ReadonlyEditor value={bookingItem.activity?.description} />
                                    </Grid>
                                </Grid>
                            )}
                            {bookingItem.activity?.extra_info?.before_go && (
                                <Grid item xs={12}>
                                    <h5>Know before you go</h5>
                                    <Grid
                                        item
                                        xs={12}
                                        style={{
                                            whiteSpace: 'pre-line',
                                            marginTop: 0,
                                            marginBottom: '10px',
                                            fontSize: '0.875rem'
                                        }}
                                    >
                                        <ReadonlyEditor
                                            value={bookingItem.activity?.extra_info?.before_go}
                                        />
                                    </Grid>
                                </Grid>
                            )}
                            {bookingItem.activity?.extra_info?.vendor_info && (
                                <Grid item xs={12}>
                                    <h5>Vendor information</h5>
                                    <Grid
                                        item
                                        xs={12}
                                        style={{
                                            whiteSpace: 'pre-line',
                                            marginTop: 0,
                                            marginBottom: '10px',
                                            fontSize: '0.875rem'
                                        }}
                                    >
                                        <ReadonlyEditor
                                            value={bookingItem.activity?.extra_info?.vendor_info}
                                        />
                                    </Grid>
                                </Grid>
                            )}
                            {bookingItem.activity?.extra_info?.pickup_location && (
                                <Grid item xs={12}>
                                    <h5>Pick-Up or Check-In Location</h5>
                                    <p>{bookingItem.activity?.extra_info?.pickup_location}</p>
                                </Grid>
                            )}
                            {onGuestPage &&
                                bookingItem.booking_item_tickets.map((ticket, idx) => {
                                    const info = ticket?.ticket_info ?? {};
                                    const guests = [info.booker, ...(info.guests ?? [])];

                                    return (
                                        <Grid item xs={12} key={idx}>
                                            <BookingFieldsTable
                                                title={`${ticket?.bookingItemTicket?.name ??
                                                    'Ticket'} — Guests`}
                                                guests={guests}
                                                barColor={
                                                    BOOKING_STATE_BORDER_COLORS[bookingItem.status]
                                                }
                                            />
                                        </Grid>
                                    );
                                })}
                            {bookingItem.booking_info?.frontend_url && (
                                <Grid item xs={12} style={{ marginTop: '1rem' }}>
                                    <Button
                                        variant="outlined"
                                        color="primary"
                                        size="small"
                                        href={bookingItem.booking_info.frontend_url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        View online voucher
                                    </Button>
                                </Grid>
                            )}
                        </Grid>
                    </Grid>
                </AccordionDetails>
            </Accordion>
        </FancyCard>
    );
}

export default function BookingTable({
    isAgent,
    isAdmin,
    bookingItems,
    onCancelFinished,
    onGuestPage
}) {
    const [open, setOpen] = useState(false);
    const { showModal } = useModals();

    if (!bookingItems) {
        return null;
    }

    return (
        <>
            {bookingItems.map(bookingItem => (
                <BookingItemRow
                    key={bookingItem.id}
                    bookingItem={bookingItem}
                    isAgent={isAgent}
                    isAdmin={isAdmin}
                    onCancelFinished={onCancelFinished}
                    onGuestPage={onGuestPage}
                    open={open}
                    setOpen={setOpen}
                    showModal={showModal}
                />
            ))}
        </>
    );
}
